import { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  X, 
  Save, 
  Star, 
  User, 
  Briefcase,
  Award,
  TrendingUp,
  FileText,
  MessageSquare,
  Brain,
  CheckCircle,
  Users,
  Heart,
  Target,
  AlertTriangle,
  Eye,
  UserCheck,
  Building,
  Crown,
  ListChecks
} from 'lucide-react';
import { cn } from "@/lib/utils";

interface Candidate {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  position: string;
  jobDescription: string;
  analysisScore?: number;
  verdict?: string;
  technicalSkills?: Array<{
    skill: string;
    proficiency: string;
    evidence: string;
  }>;
  interviewQuestions?: {
    technical: string[];
    behavioral: string[];
    cultural: string[];
    riskFactors: string[];
    redFlags: string[];
    greenFlags: string[];
  };
  interviewPerspectives?: Record<string, string>;
  projectId?: string;
  analysisId?: string;
}

interface SkillsData {
  technical: number;
  communication: number;
  problemSolving: number;
  teamwork: number;
  leadership: number;
  adaptability: number;
  creativity: number;
  timeManagement: number;
  [key: string]: number; // Add index signature
}

type InterviewPerspective = 'hr' | 'manager' | 'senior-manager' | 'c-level' | 'technical-lead';

interface InterviewFormData {
  candidateId: string;
  firstName: string;
  lastName: string;
  candidateEmail: string;
  position: string;
  jobDescription: string;
  type: string;
  perspective: InterviewPerspective;
  date: string;
  time: string;
  duration: string;
  location: string;
  interviewer: string;
  status: string;
  analysisScore: number;
  verdict: string;
  technicalSkills: Array<{
    skill: string;
    proficiency: string;
    evidence: string;
  }>;
  interviewQuestions: {
    technical: string[];
    behavioral: string[];
    cultural: string[];
  };
  interviewInsights: {
    riskFactors: string[];
    redFlags: string[];
    greenFlags: string[];
  };
  skills: SkillsData;
  overallRating: number;
  recommendation: string;
  strengths: string;
  weaknesses: string;
  notes: string;
}

interface InterviewFormProps {
  interview?: any;
  candidate?: Candidate;
  onSave: (interviewData: any) => void;
  onCancel: () => void;
}

// Helper function to clean up job description
function cleanJobDescription(jobDescription: string): string {
  if (!jobDescription) return '';
  
  return jobDescription
    // Remove repeated TopMatchTalent.com mentions
    .replace(/TopMatchTalent\.com[\s\-]*/g, '')
    // Remove "Generated with" lines
    .replace(/Generated with.*$/gm, '')
    // Remove website URLs
    .replace(/www\.TopMatchTalent\.com/g, '')
    // Remove "Page X" references
    .replace(/Page \d+/g, '')
    // Clean up multiple spaces and newlines
    .replace(/\s+/g, ' ')
    .replace(/\n\s*\n/g, '\n')
    .trim();
}

const perspectiveConfig = {
  'hr': {
    label: 'HR Manager',
    icon: Users,
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    description: 'Cultural fit, policy compliance, team dynamics',
    focus: ['Cultural alignment', 'Team dynamics', 'Communication skills', 'Work-life balance']
  },
  'manager': {
    label: 'Direct Manager',
    icon: UserCheck,
    color: 'bg-green-100 text-green-800 border-green-200',
    description: 'Day-to-day performance, task execution, collaboration',
    focus: ['Project execution', 'Problem-solving approach', 'Team collaboration', 'Daily responsibilities']
  },
  'senior-manager': {
    label: 'Senior Manager',
    icon: Building,
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    description: 'Strategic thinking, leadership potential, cross-functional impact',
    focus: ['Strategic thinking', 'Leadership potential', 'Cross-functional collaboration', 'Process improvement']
  },
  'c-level': {
    label: 'C-Level Executive',
    icon: Crown,
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    description: 'Vision alignment, business impact, organizational growth',
    focus: ['Business vision', 'Market understanding', 'Innovation potential', 'Organizational impact']
  },
  'technical-lead': {
    label: 'Technical Lead',
    icon: ListChecks,
    color: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    description: 'Technical depth, architecture decisions, code quality',
    focus: ['Technical expertise', 'Architecture decisions', 'Code quality', 'Technical mentoring']
  }
};

// Function to parse questions from a specific perspective
function parseQuestionsFromPerspective(perspectiveContent: string): {
  technical: string[];
  behavioral: string[];
  cultural: string[];
  redFlags: string[];
  greenFlags: string[];
} {
  const technical: string[] = [];
  const behavioral: string[] = [];
  const cultural: string[] = [];
  const redFlags: string[] = [];
  const greenFlags: string[] = [];

  if (!perspectiveContent) {
    return { technical, behavioral, cultural, redFlags, greenFlags };
  }

  try {
    // Split into sections by category
    const sections = perspectiveContent.split(/\d+\.\s*Question Category:/);
    
    sections.forEach(section => {
      if (!section.trim()) return;

      // Extract category and content
      const categoryMatch = section.match(/^([^(]+)\((\d+)\s*minutes?\)/);
      if (!categoryMatch) return;

      const category = categoryMatch[1].trim().toLowerCase();

      // Extract numbered questions (1. 2. 3. 4.)
      const numberedQuestionPattern = /^\s*(\d+)\.\s*(.+)$/gm;
      let questionMatch;
      
      while ((questionMatch = numberedQuestionPattern.exec(section)) !== null) {
        const questionText = questionMatch[2].trim();
        
        // Filter out section headers and focus on actual questions
        if (questionText && 
            questionText.length > 15 && 
            !questionText.toLowerCase().includes('question category') &&
            !questionText.toLowerCase().includes('minutes total') &&
            !questionText.toLowerCase().includes('red flags') &&
            !questionText.toLowerCase().includes('green flags') &&
            questionText.includes('?')) {
          
          if (category.includes('technical')) {
            technical.push(questionText);
          } else if (category.includes('behavioral')) {
            behavioral.push(questionText);
          } else if (category.includes('problem solving') || category.includes('cultural')) {
            cultural.push(questionText);
          }
        }
      }

      // Extract red flags
      const redFlagsMatch = section.match(/Red Flags:\s*([\s\S]*?)(?=\n\nGreen Flags:|Green Flags:|$)/);
      if (redFlagsMatch) {
        const flagsText = redFlagsMatch[1];
        const flags = flagsText.match(/[-•]\s*(.*?)(?=\n|$)/g);
        if (flags) {
          flags.forEach(flag => {
            const cleanFlag = flag.replace(/^[-•]\s*/, '').trim();
            if (cleanFlag && cleanFlag.length > 10) {
              redFlags.push(cleanFlag);
            }
          });
        }
      }

      // Extract green flags
      const greenFlagsMatch = section.match(/Green Flags:\s*([\s\S]*?)(?=\n\nEvaluation Criteria:|Evaluation Criteria:|$)/);
      if (greenFlagsMatch) {
        const flagsText = greenFlagsMatch[1];
        const flags = flagsText.match(/[-•]\s*(.*?)(?=\n|$)/g);
        if (flags) {
          flags.forEach(flag => {
            const cleanFlag = flag.replace(/^[-•]\s*/, '').trim();
            if (cleanFlag && cleanFlag.length > 10) {
              greenFlags.push(cleanFlag);
            }
          });
        }
      }
    });
  } catch (error) {
    console.error('Error parsing perspective questions:', error);
  }

  return { technical, behavioral, cultural, redFlags, greenFlags };
}

export function InterviewForm({ interview, candidate, onSave, onCancel }: InterviewFormProps) {
  // Determine the appropriate starting tab based on interview status
  const getInitialTab = () => {
    if (interview?.status === 'completed') {
      return 'assessment'; // Jump to results for completed interviews
    }
    // For live interviews, start with questions tab for immediate use
    return candidate ? 'questions' : 'setup';
  };
  
  const [activeTab, setActiveTab] = useState<'setup' | 'questions' | 'insights' | 'assessment'>(getInitialTab());
  
  const [formData, setFormData] = useState<InterviewFormData>(() => {
    // Initialize with candidate data immediately if available
    return {
      candidateId: candidate?.id || '',
      firstName: candidate?.firstName || '',
      lastName: candidate?.lastName || '',
      candidateEmail: candidate?.email || '',
      position: candidate?.position || '',
      jobDescription: candidate ? cleanJobDescription(candidate.jobDescription) : '',
      type: 'technical',
      perspective: 'manager',
      date: new Date().toISOString().split('T')[0],
      time: '09:00',
      duration: '60',
      location: 'Video Call',
      interviewer: '',
      status: 'in_progress', // Auto-set to in_progress for live interviews
      analysisScore: candidate?.analysisScore || 0,
      verdict: candidate?.verdict || '',
      technicalSkills: candidate?.technicalSkills || [],
      interviewQuestions: {
        technical: [],
        behavioral: [],
        cultural: []
      },
      interviewInsights: {
        riskFactors: candidate?.interviewQuestions?.riskFactors || [],
        redFlags: candidate?.interviewQuestions?.redFlags || [],
        greenFlags: candidate?.interviewQuestions?.greenFlags || []
      },
      skills: {
        technical: 5,
        communication: 5,
        problemSolving: 5,
        teamwork: 5,
        leadership: 5,
        adaptability: 5,
        creativity: 5,
        timeManagement: 5
      },
      overallRating: 5,
      recommendation: '',
      strengths: '',
      weaknesses: '',
      notes: ''
    };
  });

  // Auto-save functionality for live interviews
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const autoSave = useCallback(async () => {
    if (interview?.status === 'completed') return; // Don't auto-save completed interviews
    
    setAutoSaveStatus('saving');
    try {
      const interviewData = {
        candidateId: formData.candidateId,
        candidateName: `${formData.firstName} ${formData.lastName}`.trim(),
        firstName: formData.firstName,
        lastName: formData.lastName,
        candidateEmail: formData.candidateEmail,
        position: formData.position,
        jobDescription: formData.jobDescription,
        type: formData.type,
        status: formData.status,
        overallRating: formData.overallRating,
        recommendation: formData.recommendation,
        skills: formData.skills,
        notes: formData.notes,
        projectId: candidate?.projectId,
        analysisId: candidate?.analysisId,
        analysisScore: formData.analysisScore,
        verdict: formData.verdict
      };

      await onSave(interviewData);
      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus('idle'), 2000);
    } catch (error) {
      setAutoSaveStatus('error');
      setTimeout(() => setAutoSaveStatus('idle'), 3000);
    }
  }, [formData, interview, candidate, onSave]);

  // Trigger auto-save when form data changes
  useEffect(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    autoSaveTimeoutRef.current = setTimeout(() => {
      if (formData.notes || formData.overallRating !== 5 || formData.recommendation) {
        autoSave();
      }
    }, 3000); // Auto-save after 3 seconds of inactivity

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [formData.notes, formData.overallRating, formData.recommendation, autoSave]);

  // Form submission handler
  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    
    // Include analysis data in the saved interview
    const interviewData = {
      ...formData,
      id: interview?.id || `interview-${Date.now()}`,
      projectId: candidate?.projectId,
      analysisId: candidate?.analysisId,
      createdAt: interview?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    onSave(interviewData);
  }, [formData, interview, candidate, onSave]);

  // Keyboard shortcuts for live interview efficiency
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't interfere with typing in inputs
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        // Allow Ctrl+Enter to save even when in input fields
        if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
          event.preventDefault();
          handleSubmit(event as any);
        }
        return;
      }

      // Tab navigation between sections
      if (event.key === '1' && !event.ctrlKey && !event.metaKey) {
        event.preventDefault();
        setActiveTab('setup');
      } else if (event.key === '2' && !event.ctrlKey && !event.metaKey) {
        event.preventDefault();
        setActiveTab('questions');
      } else if (event.key === '3' && !event.ctrlKey && !event.metaKey) {
        event.preventDefault();
        setActiveTab('insights');
      } else if (event.key === '4' && !event.ctrlKey && !event.metaKey) {
        event.preventDefault();
        setActiveTab('assessment');
      }

      // Ctrl+Enter to save interview
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        event.preventDefault();
        handleSubmit(event as any);
      }

      // Escape to cancel
      if (event.key === 'Escape' && !event.ctrlKey && !event.metaKey) {
        event.preventDefault();
        onCancel();
      }

      // Quick rating shortcuts (Ctrl + number for overall rating)
      if ((event.ctrlKey || event.metaKey) && event.key >= '1' && event.key <= '9') {
        event.preventDefault();
        const rating = parseInt(event.key);
        if (rating >= 1 && rating <= 10) {
          setFormData(prev => ({ ...prev, overallRating: rating }));
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleSubmit, onCancel]);

  // State for current perspective questions
  const [currentPerspectiveQuestions, setCurrentPerspectiveQuestions] = useState<{
    technical: string[];
    behavioral: string[];
    cultural: string[];
    redFlags: string[];
    greenFlags: string[];
  }>({
    technical: [],
    behavioral: [],
    cultural: [],
    redFlags: [],
    greenFlags: []
  });

  // Update perspective questions when perspective or candidate changes
  useEffect(() => {
    console.log('🔄 Updating perspective questions...', {
      hasPerspectives: !!candidate?.interviewPerspectives,
      perspectiveKeys: candidate?.interviewPerspectives ? Object.keys(candidate.interviewPerspectives) : [],
      hasInterviewQuestions: !!candidate?.interviewQuestions,
      perspective: formData.perspective
    });

    if (candidate?.interviewPerspectives && Object.keys(candidate.interviewPerspectives).length > 0) {
      const perspectiveContent = candidate.interviewPerspectives[formData.perspective];
      
      if (!perspectiveContent) {
        console.warn(`⚠️ NO PERSPECTIVE CONTENT for ${formData.perspective}`);
        console.warn('Available perspectives:', Object.keys(candidate.interviewPerspectives));
        
        // Try to use any available perspective as fallback
        const availablePerspectives = Object.keys(candidate.interviewPerspectives).filter(
          key => candidate.interviewPerspectives?.[key] && candidate.interviewPerspectives[key].trim().length > 0
        );
        
        if (availablePerspectives.length > 0) {
          const fallbackPerspective = candidate.interviewPerspectives[availablePerspectives[0]];
          console.log(`🔄 Using fallback perspective: ${availablePerspectives[0]}`);
          const perspectiveQuestions = parseQuestionsFromPerspective(fallbackPerspective);
          setCurrentPerspectiveQuestions(perspectiveQuestions);
        } else {
          // Fall back to basic interview questions
          console.warn('⚠️ No valid perspectives, falling back to basic interview questions');
          setCurrentPerspectiveQuestions({
            technical: candidate.interviewQuestions?.technical || [],
            behavioral: candidate.interviewQuestions?.behavioral || [],
            cultural: candidate.interviewQuestions?.cultural || [],
            redFlags: candidate.interviewQuestions?.redFlags || [],
            greenFlags: candidate.interviewQuestions?.greenFlags || []
          });
        }
        return;
      }

      console.log(`✅ Loading questions for ${formData.perspective} perspective`);
      const perspectiveQuestions = parseQuestionsFromPerspective(perspectiveContent);
      setCurrentPerspectiveQuestions(perspectiveQuestions);
    } else if (candidate?.interviewQuestions) {
      // Use basic interview questions if no perspectives data
      console.log('💡 Using basic interview questions (no perspectives data)');
      setCurrentPerspectiveQuestions({
        technical: candidate.interviewQuestions.technical || [],
        behavioral: candidate.interviewQuestions.behavioral || [],
        cultural: candidate.interviewQuestions.cultural || [],
        redFlags: candidate.interviewQuestions.redFlags || [],
        greenFlags: candidate.interviewQuestions.greenFlags || []
      });
    } else {
      // No interview data at all - set empty but allow form to continue
      console.warn('⚠️ NO INTERVIEW DATA - setting empty questions');
      setCurrentPerspectiveQuestions({
        technical: [],
        behavioral: [],
        cultural: [],
        redFlags: [],
        greenFlags: []
      });
    }
  }, [candidate?.interviewPerspectives, candidate?.interviewQuestions, formData.perspective]);

  // Update form when candidate or interview changes
  useEffect(() => {
    if (interview) {
      console.log('📝 Loading existing interview data:', {
        id: interview.id,
        candidateName: interview.candidateName,
        status: interview.status,
        hasRatings: !!interview.skills
      });
      
      setFormData(prev => ({
        ...prev,
        candidateId: interview.candidateId || '',
        firstName: interview.firstName || '',
        lastName: interview.lastName || '',
        candidateEmail: interview.candidateEmail || '',
        position: interview.position || '',
        jobDescription: interview.jobDescription || '',
        type: interview.type || 'technical',
        status: interview.status || 'scheduled',
        overallRating: interview.overallRating || 5,
        recommendation: interview.recommendation || '',
        skills: interview.skills || {
          technical: 5,
          communication: 5,
          problemSolving: 5,
          teamwork: 5,
          leadership: 5,
          adaptability: 5,
          creativity: 5,
          timeManagement: 5
        },
        notes: interview.notes || '',
        analysisScore: interview.analysisScore || 0,
        verdict: interview.verdict || ''
      }));
    } else if (candidate) {
      console.log('📝 Updating form with candidate data:', {
        id: candidate.id,
        name: `${candidate.firstName} ${candidate.lastName}`,
        position: candidate.position,
        hasInterviewPerspectives: !!candidate.interviewPerspectives,
        hasInterviewQuestions: !!candidate.interviewQuestions
      });
      
      setFormData(prev => ({
        ...prev,
        candidateId: candidate.id || '',
        firstName: candidate.firstName || '',
        lastName: candidate.lastName || '',
        candidateEmail: candidate.email || '',
        position: candidate.position || '',
        jobDescription: candidate.jobDescription ? cleanJobDescription(candidate.jobDescription) : '',
        analysisScore: candidate.analysisScore || 0,
        verdict: candidate.verdict || '',
        interviewInsights: {
          riskFactors: candidate.interviewQuestions?.riskFactors || [],
          redFlags: candidate.interviewQuestions?.redFlags || [],
          greenFlags: candidate.interviewQuestions?.greenFlags || []
        },
        technicalSkills: candidate.technicalSkills || []
      }));
      
      console.log('✅ Form data updated successfully');
    }
  }, [candidate, interview]);

  // Auto-calculate overall rating when skills change
  useEffect(() => {
    const skillValues = Object.values(formData.skills).filter(val => val > 0);
    if (skillValues.length > 0) {
      const averageRating = skillValues.reduce((sum, val) => sum + val, 0) / skillValues.length;
      const roundedRating = Math.round(averageRating * 10) / 10;
      
      if (formData.overallRating !== roundedRating) {
        setFormData(prev => ({
          ...prev,
          overallRating: roundedRating
        }));
      }
    }
  }, [formData.skills]);

  const handleSkillChange = (skillName: string, value: number[]) => {
    setFormData(prev => {
      const updatedSkills = {
        ...prev.skills,
        [skillName]: value[0]
      };
      
      // Calculate overall rating as average of all skills
      const skillValues = Object.values(updatedSkills).filter(val => val > 0);
      const averageRating = skillValues.length > 0 
        ? skillValues.reduce((sum, val) => sum + val, 0) / skillValues.length 
        : 0;
      
      return {
        ...prev,
        skills: updatedSkills,
        overallRating: Math.round(averageRating * 10) / 10 // Round to 1 decimal place
      };
    });
  };



  const getSkillColor = (score: number) => {
    if (score >= 8) return 'text-green-600';
    if (score >= 6) return 'text-yellow-600';
    if (score >= 4) return 'text-orange-600';
    return 'text-red-600';
  };

  const getRecommendationColor = (recommendation: string) => {
    switch (recommendation) {
      case 'hire': return 'bg-green-50 text-green-700 border-green-200';
      case 'maybe': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'no': return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getVerdictColor = (verdict: string) => {
    switch (verdict) {
      case 'STRONG_FIT': return 'bg-green-50 text-green-700 border-green-200';
      case 'POTENTIAL_FIT': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'NEEDS_CONSIDERATION': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'NOT_RECOMMENDED': return 'bg-red-50 text-red-700 border-red-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const coreSkills = [
    { key: 'technical', label: 'Technical Skills' },
    { key: 'communication', label: 'Communication' },
    { key: 'problemSolving', label: 'Problem Solving' },
    { key: 'teamwork', label: 'Teamwork' },
    { key: 'leadership', label: 'Leadership' }
  ];

  const additionalSkills = [
    { key: 'adaptability', label: 'Adaptability' },
    { key: 'creativity', label: 'Creativity' },
    { key: 'timeManagement', label: 'Time Management' }
  ];

  const selectedPerspective = perspectiveConfig[formData.perspective];

  const renderQuestionsTab = () => (
    <TabsContent value="questions" className="space-y-6">
      {/* Technical Questions */}
      {currentPerspectiveQuestions.technical.length > 0 && (
        <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
              <Brain className="h-5 w-5 mr-2 text-blue-600" />
              Technical Questions
              <Badge variant="secondary" className="ml-2 text-xs">
                {selectedPerspective.label} Perspective
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4">
              {currentPerspectiveQuestions.technical.map((question: string, index: number) => (
                <div key={index} className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-start gap-3">
                    <Badge variant="outline" className="text-xs font-mono bg-blue-100 text-blue-700 border-blue-300">
                      T{index + 1}
                    </Badge>
                    <p className="text-sm text-blue-900 flex-1">{question}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Behavioral Questions */}
      {currentPerspectiveQuestions.behavioral.length > 0 && (
        <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
              <Users className="h-5 w-5 mr-2 text-green-600" />
              Behavioral Questions
              <Badge variant="secondary" className="ml-2 text-xs">
                {selectedPerspective.label} Perspective
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4">
              {currentPerspectiveQuestions.behavioral.map((question: string, index: number) => (
                <div key={index} className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-start gap-3">
                    <Badge variant="outline" className="text-xs font-mono bg-green-100 text-green-700 border-green-300">
                      B{index + 1}
                    </Badge>
                    <p className="text-sm text-green-900 flex-1">{question}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cultural Questions */}
      {currentPerspectiveQuestions.cultural.length > 0 && (
        <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
              <Heart className="h-5 w-5 mr-2 text-purple-600" />
              Cultural Fit & Problem Solving
              <Badge variant="secondary" className="ml-2 text-xs">
                {selectedPerspective.label} Perspective
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4">
              {currentPerspectiveQuestions.cultural.map((question: string, index: number) => (
                <div key={index} className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-start gap-3">
                    <Badge variant="outline" className="text-xs font-mono bg-purple-100 text-purple-700 border-purple-300">
                      C{index + 1}
                    </Badge>
                    <p className="text-sm text-purple-900 flex-1">{question}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Questions Available */}
      {currentPerspectiveQuestions.technical.length === 0 && 
       currentPerspectiveQuestions.behavioral.length === 0 && 
       currentPerspectiveQuestions.cultural.length === 0 && (
        <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
          <CardContent className="p-12 text-center">
            <MessageSquare className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No Questions Available</h3>
            <p className="text-slate-600">
              No perspective-specific questions found for {selectedPerspective.label}. 
              Try selecting a different perspective or complete a resume analysis first.
            </p>
          </CardContent>
        </Card>
      )}
    </TabsContent>
  );

  // Debug: Log current form data when component renders
  console.log('🎯 InterviewForm render:', {
    candidateId: formData.candidateId,
    firstName: formData.firstName,
    lastName: formData.lastName,
    position: formData.position,
    analysisScore: formData.analysisScore,
    verdict: formData.verdict,
    hasCandidate: !!candidate
  });

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[92vh] overflow-hidden border border-slate-200 flex flex-col">
        {/* Header */}
        <div className="bg-slate-50 border-b border-slate-200 px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center flex-shrink-0">
                <User className="h-6 w-6 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-2xl font-bold text-slate-900 truncate">
                  {interview 
                    ? (interview.status === 'completed' ? 'Interview Review' : 'Edit Interview')
                    : 'New Interview'
                  }
                </h2>
                <p className="text-slate-600 mt-1 truncate">
                  {interview?.status === 'completed' 
                    ? `Review results and notes for ${formData.firstName} ${formData.lastName}`
                    : formData.firstName && formData.lastName 
                      ? `Live interview with ${formData.firstName} ${formData.lastName}`
                      : 'Complete candidate assessment with skill ratings'
                  }
                </p>
                
                {/* Auto-save Status & Interview Info */}
                <div className="flex items-center gap-4 mt-2">
                  {interview?.status === 'completed' ? (
                    <>
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-xs text-slate-500">Completed on {new Date(interview.createdAt).toLocaleDateString()}</span>
                      </div>
                      {interview.overallRating && (
                        <div className="flex items-center gap-1">
                          <Star className="h-3 w-3 text-yellow-500 fill-current" />
                          <span className="text-xs font-medium text-slate-700">{interview.overallRating}/10</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {/* Live Interview Status */}
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                        <span className="text-xs text-slate-500">Live Interview</span>
                      </div>
                      
                      {/* Auto-save Status */}
                      {autoSaveStatus === 'saving' && (
                        <div className="flex items-center gap-1 text-blue-600">
                          <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-xs">Auto-saving...</span>
                        </div>
                      )}
                      {autoSaveStatus === 'saved' && (
                        <div className="flex items-center gap-1 text-green-600">
                          <CheckCircle className="w-3 h-3" />
                          <span className="text-xs">Auto-saved</span>
                        </div>
                      )}
                      {autoSaveStatus === 'error' && (
                        <div className="flex items-center gap-1 text-red-600">
                          <AlertTriangle className="w-3 h-3" />
                          <span className="text-xs">Save failed</span>
                        </div>
                      )}
                      
                      {/* Quick Shortcuts Hint */}
                      <div className="text-xs text-slate-400 hidden lg:block">
                        Press Tab to navigate • Ctrl+Enter to save
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <Button
              onClick={onCancel}
              variant="outline"
              size="sm"
              className="rounded-lg border-slate-200 hover:bg-slate-100 flex-shrink-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Form Content with Tabs */}
        <div className="flex-1 overflow-hidden">
          <form onSubmit={handleSubmit} className="h-full flex flex-col">
            <div className="overflow-y-auto flex-1 p-6">
              <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)} className="space-y-6">
              <TabsList className="grid w-full grid-cols-4 bg-slate-100 rounded-lg p-1">
                <TabsTrigger value="setup" className="rounded-md flex items-center gap-2">
                  <span className="text-xs">1</span>
                  {interview?.status === 'completed' ? 'Overview' : 'Setup'}
                  {formData.firstName && formData.lastName && (
                    <CheckCircle className="w-3 h-3 text-green-600" />
                  )}
                </TabsTrigger>
                <TabsTrigger value="questions" className="rounded-md flex items-center gap-2">
                  <span className="text-xs">2</span>
                  Questions
                  <span className="text-xs text-slate-500">(Press 2)</span>
                </TabsTrigger>
                <TabsTrigger value="insights" className="rounded-md flex items-center gap-2">
                  <span className="text-xs">3</span>
                  Insights
                  <span className="text-xs text-slate-500">(Press 3)</span>
                </TabsTrigger>
                <TabsTrigger value="assessment" className="rounded-md flex items-center gap-2">
                  <span className="text-xs">4</span>
                  {interview?.status === 'completed' ? 'Results' : 'Assessment'}
                  {formData.recommendation && (
                    <CheckCircle className="w-3 h-3 text-green-600" />
                  )}
                  <span className="text-xs text-slate-500">(Press 4)</span>
                </TabsTrigger>
              </TabsList>

              {/* Setup Tab */}
              <TabsContent value="setup" className="space-y-6">
                {/* Candidate Information */}
                <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
                      <Briefcase className="h-5 w-5 mr-2 text-slate-600" />
                      Candidate Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Analysis Summary */}
                    {(formData.analysisScore > 0 || formData.verdict) && (
                      <Alert className="bg-slate-50 border-slate-200">
                        <CheckCircle className="h-4 w-4" />
                        <AlertDescription>
                          <div className="flex items-center justify-between">
                            <span>Analysis Score: <strong>{formData.analysisScore}/10</strong></span>
                            {formData.verdict && (
                              <Badge className={cn("text-xs", getVerdictColor(formData.verdict))}>
                                {formData.verdict.replace('_', ' ')}
                              </Badge>
                            )}
                          </div>
                        </AlertDescription>
                      </Alert>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="firstName" className="text-sm font-medium text-slate-700">
                          First Name
                        </Label>
                        <Input
                          id="firstName"
                          value={formData.firstName}
                          onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                          placeholder="Enter first name"
                          className="rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400"
                          disabled={interview?.status === 'completed'}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName" className="text-sm font-medium text-slate-700">
                          Last Name
                        </Label>
                        <Input
                          id="lastName"
                          value={formData.lastName}
                          onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                          placeholder="Enter last name"
                          className="rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400"
                          disabled={interview?.status === 'completed'}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="candidateEmail" className="text-sm font-medium text-slate-700">
                          Email
                        </Label>
                        <Input
                          id="candidateEmail"
                          type="email"
                          value={formData.candidateEmail}
                          onChange={(e) => setFormData(prev => ({ ...prev, candidateEmail: e.target.value }))}
                          placeholder="candidate@example.com"
                          className="rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400"
                          disabled={interview?.status === 'completed'}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="position" className="text-sm font-medium text-slate-700">
                          Position
                        </Label>
                        <Input
                          id="position"
                          value={formData.position}
                          onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
                          placeholder="e.g., Senior Software Engineer"
                          className="rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400"
                          disabled={interview?.status === 'completed'}
                          required
                        />
                      </div>
                    </div>

                    {/* Job Description */}
                    {formData.jobDescription && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Job Description
                        </Label>
                        <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 text-sm text-slate-700 max-h-32 overflow-y-auto">
                          {formData.jobDescription}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Interview Configuration */}
                <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
                      <Eye className="h-5 w-5 mr-2 text-slate-600" />
                      Interview Configuration
                      {interview?.status === 'completed' && (
                        <Badge variant="secondary" className="ml-2 text-xs">
                          Read Only
                        </Badge>
                      )}
                    </CardTitle>
                    {interview?.status === 'completed' && (
                      <p className="text-sm text-slate-500 mt-1">
                        Interview configuration is locked for completed interviews
                      </p>
                    )}
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {/* Interview Type */}
                      <div className="space-y-2">
                        <Label htmlFor="type" className="text-sm font-medium text-slate-700">
                          Interview Type
                        </Label>
                        <Select 
                          value={formData.type} 
                          onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
                          disabled={interview?.status === 'completed'}
                        >
                          <SelectTrigger className="rounded-lg border-slate-200">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="screening">Screening Interview</SelectItem>
                            <SelectItem value="technical">Technical Interview</SelectItem>
                            <SelectItem value="final">Final Interview</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Interview Status */}
                      <div className="space-y-2">
                        <Label htmlFor="status" className="text-sm font-medium text-slate-700">
                          Interview Status
                        </Label>
                        <Select 
                          value={formData.status} 
                          onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                        >
                          <SelectTrigger className="rounded-lg border-slate-200">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="scheduled">Scheduled</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Interview Perspective */}
                      <div className="space-y-2">
                        <Label htmlFor="perspective" className="text-sm font-medium text-slate-700">
                          Interview Perspective
                        </Label>
                        <Select 
                          value={formData.perspective} 
                          onValueChange={(value) => setFormData(prev => ({ ...prev, perspective: value as InterviewPerspective }))}
                          disabled={interview?.status === 'completed'}
                        >
                          <SelectTrigger className="rounded-lg border-slate-200">
                            <SelectValue placeholder="Select perspective" />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(perspectiveConfig).map(([key, config]) => (
                              <SelectItem key={key} value={key}>
                                <div className="flex items-center gap-2">
                                  <config.icon className="h-4 w-4" />
                                  {config.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Perspective Details */}
                    <div className={cn("p-4 rounded-lg border", selectedPerspective.color)}>
                      <div className="flex items-start gap-3">
                        <selectedPerspective.icon className="h-5 w-5 mt-0.5" />
                        <div>
                          <h4 className="font-medium">{selectedPerspective.label}</h4>
                          <p className="text-sm mt-1 opacity-90">{selectedPerspective.description}</p>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {selectedPerspective.focus.map((item, index) => (
                              <Badge key={index} variant="secondary" className="text-xs">
                                {item}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Questions Tab */}
              {renderQuestionsTab()}

              {/* Insights Tab */}
              <TabsContent value="insights" className="space-y-6">
                {/* Risk Factors (separate section) */}
                {formData.interviewInsights.riskFactors && formData.interviewInsights.riskFactors.length > 0 && (
                  <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
                    <CardHeader className="pb-4">
                      <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
                        <Target className="h-5 w-5 mr-2 text-red-600" />
                        Risk Factors to Address
                        <Badge variant="destructive" className="ml-2 text-xs">
                          From Analysis
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {formData.interviewInsights.riskFactors.map((risk: string, index: number) => (
                        <div key={index} className="p-4 bg-red-50 rounded-lg border border-red-200">
                          <div className="flex items-start gap-3">
                            <Badge variant="outline" className="text-xs font-mono bg-red-100 text-red-700 border-red-300">
                              R{index + 1}
                            </Badge>
                            <p className="text-sm text-red-700 flex-1">{risk}</p>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Red and Green Flags Section - Perspective Specific */}
                {(currentPerspectiveQuestions.redFlags.length > 0 || currentPerspectiveQuestions.greenFlags.length > 0 || 
                  formData.interviewInsights.redFlags.length > 0 || formData.interviewInsights.greenFlags.length > 0) && (
                  <Card className="border border-slate-200 bg-white shadow-sm">
                    <CardHeader className="bg-slate-50 border-b border-slate-200">
                      <CardTitle className="flex items-center text-slate-900">
                        <AlertTriangle className="w-5 h-5 mr-2 text-amber-500" />
                        Assessment Indicators
                        <Badge variant="secondary" className="ml-2 text-xs">
                          {selectedPerspective.label} Perspective
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Red Flags - Perspective Specific + General */}
                        {(currentPerspectiveQuestions.redFlags.length > 0 || formData.interviewInsights.redFlags.length > 0) && (
                          <div>
                            <h4 className="font-semibold text-red-600 mb-3 flex items-center">
                              <AlertTriangle className="w-4 h-4 mr-1" />
                              Red Flags to Watch For
                            </h4>
                            <div className="space-y-2">
                              {/* Perspective-specific red flags first */}
                              {currentPerspectiveQuestions.redFlags.map((flag, index) => (
                                <div key={`perspective-red-${index}`} className="flex items-start space-x-2 p-3 bg-red-50 border-l-4 border-red-300 rounded-r">
                                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                                  <p className="text-sm text-red-900 font-medium">{flag}</p>
                                </div>
                              ))}
                              {/* General red flags */}
                              {formData.interviewInsights.redFlags.map((flag, index) => (
                                <div key={`general-red-${index}`} className="flex items-start space-x-2 p-3 bg-red-50 border-l-4 border-red-200 rounded-r">
                                  <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                                  <p className="text-sm text-red-800">{flag}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Green Flags - Perspective Specific + General */}
                        {(currentPerspectiveQuestions.greenFlags.length > 0 || formData.interviewInsights.greenFlags.length > 0) && (
                          <div>
                            <h4 className="font-semibold text-green-600 mb-3 flex items-center">
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Green Flags to Look For
                            </h4>
                            <div className="space-y-2">
                              {/* Perspective-specific green flags first */}
                              {currentPerspectiveQuestions.greenFlags.map((flag, index) => (
                                <div key={`perspective-green-${index}`} className="flex items-start space-x-2 p-3 bg-green-50 border-l-4 border-green-300 rounded-r">
                                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                                  <p className="text-sm text-green-900 font-medium">{flag}</p>
                                </div>
                              ))}
                              {/* General green flags */}
                              {formData.interviewInsights.greenFlags.map((flag, index) => (
                                <div key={`general-green-${index}`} className="flex items-start space-x-2 p-3 bg-green-50 border-l-4 border-green-200 rounded-r">
                                  <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                                  <p className="text-sm text-green-800">{flag}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Assessment Tab */}
              <TabsContent value="assessment" className="space-y-6">
                {interview?.status === 'completed' && (
                  <Alert className="bg-green-50 border-green-200">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-800">
                      <div className="flex items-center justify-between">
                        <span>
                          <strong>Interview Completed</strong> - Final assessment and ratings
                        </span>
                        {interview.recommendation && (
                          <Badge className={cn("text-xs", getRecommendationColor(interview.recommendation))}>
                            {interview.recommendation.toUpperCase()}
                          </Badge>
                        )}
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
                {/* Technical Skills from Analysis */}
                {formData.technicalSkills && formData.technicalSkills.length > 0 && (
                  <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
                    <CardHeader className="pb-4">
                      <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
                        <Brain className="h-5 w-5 mr-2 text-slate-600" />
                        Technical Skills Overview
                        <Badge variant="secondary" className="ml-2 text-xs">
                          From Resume Analysis
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {formData.technicalSkills.slice(0, 6).map((skill: any, index: number) => (
                          <div key={index} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-slate-900">{skill.skill}</span>
                                <Badge variant="outline" className="text-xs">
                                  {skill.proficiency}
                                </Badge>
                              </div>
                              <p className="text-xs text-slate-600">{skill.evidence}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      {formData.technicalSkills.length > 6 && (
                        <p className="text-xs text-slate-500 text-center">
                          +{formData.technicalSkills.length - 6} more skills identified
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Core Skills Assessment */}
                <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
                      <Star className="h-5 w-5 mr-2 text-slate-600" />
                      Core Skills Assessment
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {coreSkills.map((skill) => (
                      <div key={skill.key} className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-medium text-slate-700">{skill.label}</Label>
                          <span className={cn("font-bold text-lg", getSkillColor(formData.skills[skill.key]))}>
                            {formData.skills[skill.key]}/10
                          </span>
                        </div>
                        
                        {/* Quick Rating Buttons for Live Interview */}
                        <div className="flex items-center gap-1 mb-2">
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
                            <button
                              key={rating}
                              type="button"
                              onClick={() => handleSkillChange(skill.key, [rating])}
                              disabled={interview?.status === 'completed'}
                              className={cn(
                                "w-8 h-8 text-xs font-medium rounded-md border transition-all hover:scale-105",
                                formData.skills[skill.key] === rating
                                  ? "bg-slate-900 text-white border-slate-900"
                                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400 hover:bg-slate-50"
                              )}
                            >
                              {rating}
                            </button>
                          ))}
                        </div>
                        
                        <Slider
                          value={[formData.skills[skill.key]]}
                          onValueChange={(value) => handleSkillChange(skill.key, value)}
                          max={10}
                          min={1}
                          step={1}
                          className="w-full"
                          disabled={interview?.status === 'completed'}
                        />
                        <div className="flex justify-between text-xs text-slate-500">
                          <span>Needs Improvement (1-3)</span>
                          <span>Good (4-7)</span>
                          <span>Excellent (8-10)</span>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Additional Skills */}
                <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
                      <Award className="h-5 w-5 mr-2 text-slate-600" />
                      Additional Skills
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {additionalSkills.map((skill) => (
                      <div key={skill.key} className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-medium text-slate-700">{skill.label}</Label>
                          <span className={cn("font-bold text-lg", getSkillColor(formData.skills[skill.key]))}>
                            {formData.skills[skill.key]}/10
                          </span>
                        </div>
                        
                        {/* Quick Rating Buttons for Live Interview */}
                        <div className="flex items-center gap-1 mb-2">
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
                            <button
                              key={rating}
                              type="button"
                              onClick={() => handleSkillChange(skill.key, [rating])}
                              disabled={interview?.status === 'completed'}
                              className={cn(
                                "w-8 h-8 text-xs font-medium rounded-md border transition-all hover:scale-105",
                                formData.skills[skill.key] === rating
                                  ? "bg-slate-900 text-white border-slate-900"
                                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400 hover:bg-slate-50"
                              )}
                            >
                              {rating}
                            </button>
                          ))}
                        </div>
                        
                        <Slider
                          value={[formData.skills[skill.key]]}
                          onValueChange={(value) => handleSkillChange(skill.key, value)}
                          max={10}
                          min={1}
                          step={1}
                          className="w-full"
                          disabled={interview?.status === 'completed'}
                        />
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Overall Assessment */}
                <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
                      <TrendingUp className="h-5 w-5 mr-2 text-slate-600" />
                      Overall Assessment
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-700">Overall Rating</Label>
                        <div className="space-y-3">
                          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-slate-600">Calculated Average</span>
                              <span className={cn("font-bold text-2xl", getSkillColor(formData.overallRating))}>
                                {formData.overallRating.toFixed(1)}/10
                              </span>
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                              Automatically calculated from skill ratings
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <Label className="text-sm font-medium text-slate-700">Final Recommendation</Label>
                        
                        {/* Quick Recommendation Buttons */}
                        <div className="grid grid-cols-3 gap-2 mb-3">
                          {[
                            { value: 'strong_hire', label: 'Strong Hire', color: 'bg-green-600 hover:bg-green-700 text-white', icon: '🌟' },
                            { value: 'hire', label: 'Hire', color: 'bg-blue-600 hover:bg-blue-700 text-white', icon: '👍' },
                            { value: 'maybe', label: 'Maybe', color: 'bg-yellow-600 hover:bg-yellow-700 text-white', icon: '🤔' },
                            { value: 'no_hire', label: 'No Hire', color: 'bg-red-600 hover:bg-red-700 text-white', icon: '👎' }
                          ].map((rec) => (
                            <button
                              key={rec.value}
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, recommendation: rec.value }))}
                              className={cn(
                                "p-3 rounded-lg font-medium text-sm transition-all hover:scale-105 border-2",
                                formData.recommendation === rec.value
                                  ? rec.color + " border-transparent"
                                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                              )}
                            >
                              <span className="mr-2">{rec.icon}</span>
                              {rec.label}
                            </button>
                          ))}
                        </div>
                        
                        <Select 
                          value={formData.recommendation} 
                          onValueChange={(value) => setFormData(prev => ({ ...prev, recommendation: value }))}
                        >
                          <SelectTrigger className="rounded-lg border-slate-200">
                            <SelectValue placeholder="Select recommendation" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="strong_hire">🌟 Strong Hire</SelectItem>
                            <SelectItem value="hire">👍 Hire</SelectItem>
                            <SelectItem value="maybe">🤔 Maybe</SelectItem>
                            <SelectItem value="no_hire">👎 No Hire</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="strengths" className="text-sm font-medium text-slate-700">
                          Key Strengths
                        </Label>
                        <Textarea
                          id="strengths"
                          value={formData.strengths}
                          onChange={(e) => setFormData(prev => ({ ...prev, strengths: e.target.value }))}
                          placeholder="What are the candidate's main strengths?"
                          className="rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400"
                          rows={3}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="weaknesses" className="text-sm font-medium text-slate-700">
                          Areas for Improvement
                        </Label>
                        <Textarea
                          id="weaknesses"
                          value={formData.weaknesses}
                          onChange={(e) => setFormData(prev => ({ ...prev, weaknesses: e.target.value }))}
                          placeholder="What areas could be improved?"
                          className="rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400"
                          rows={3}
                        />
                      </div>
                    </div>

                    <div className="space-y-3">
                      <Label htmlFor="notes" className="text-sm font-medium text-slate-700 flex items-center gap-2">
                        <MessageSquare className="h-4 w-4" />
                        Live Interview Notes
                        <Badge variant="secondary" className="text-xs">Auto-saved</Badge>
                      </Label>
                      
                      {/* Quick Note Templates for Live Interviews */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {[
                          "✅ Strong technical answer",
                          "💡 Great problem-solving approach", 
                          "🤝 Excellent communication",
                          "⚠️ Needs clarification on...",
                          "❓ Follow up required",
                          "🎯 Perfect culture fit"
                        ].map((template) => (
                          <button
                            key={template}
                            type="button"
                            onClick={() => {
                              const currentNotes = formData.notes;
                              const newNote = currentNotes ? `${currentNotes}\n• ${template}` : `• ${template}`;
                              setFormData(prev => ({ ...prev, notes: newNote }));
                            }}
                            className="text-xs px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-full border border-slate-200 transition-colors"
                          >
                            {template}
                          </button>
                        ))}
                      </div>
                      
                      <Textarea
                        id="notes"
                        value={formData.notes}
                        onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Type your observations as the interview progresses... Auto-saves every 3 seconds."
                        className="rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400 text-sm"
                        rows={6}
                      />
                      <p className="text-xs text-slate-500">
                        💡 Tip: Use the quick templates above or press Ctrl+Enter to save anytime
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
            </div>

            {/* Footer */}
            <div className="border-t border-slate-200 px-6 py-4 bg-slate-50">
              <div className="flex items-center justify-end gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  className="rounded-lg border-slate-200 hover:bg-slate-100"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {interview?.status === 'completed' ? 'Update Notes' : 'Save Interview'}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
} 